import { useState, useEffect, useMemo } from "react";
import { II } from "../../components/common/Icons";
import api from "../../services/api";
import HrModal from "./HrModal";
import DocumentVerification from "../../components/common/DocumentVerification";
import { useHRData } from "../../context/HRContext";import "../../assets/styles/HR/InternsPg.css";


// ── Monthly CSV helpers ──
const _mKey = (d) => {
  if (!d) return null;
  try { const dt = new Date(d); if(isNaN(dt)) return null; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const _mLabel = (k) => {
  if (!k) return "";
  const [y, m] = k.split("-");
  return new Date(Number(y), Number(m)-1, 1).toLocaleDateString("en-IN", { month:"long", year:"numeric" });
};
const _fmtFull = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; }
};

function exportInternsCSV(rows, filename) {
  const headers = ["Intern ID","Name","Email","Department","College","Start Date","End Date","Manager","Status","Docs","Added On"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    lines.push([
      r.intern_id || r.id, r.name, r.email||"",
      r.department||r.dept||"", r.college||"",
      r.startDate ? _fmtFull(r.startDate) : "—",
      r.endDate   ? _fmtFull(r.endDate)   : "—",
      r.manager||"", r.status||"",
      `${r.docs||0}/${r.total||11}`,
      r.user_created_at ? _fmtFull(r.user_created_at) : "—"
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function InternsPg() {
  const { interns, refreshInterns } = useHRData();
  const [internList, setInternList] = useState([]);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredModal, setShowCredModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [rejectDocModal, setRejectDocModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [resendModal, setResendModal] = useState(null);
  const [resendReason, setResendReason] = useState("");
  const [resendSections, setResendSections] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [monthFilter, setMonthFilter] = useState("all");
  const [showMonthDrop, setShowMonthDrop] = useState(false);
  const [managerModal, setManagerModal] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [designationModal, setDesignationModal] = useState(null);
  const [newDesignation, setNewDesignation] = useState("");

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    college: "",
    department: "Engineering",
    role: "Intern",
    startDate: "",
    endDate: "",
    manager: ""
  });

  // Credentials state
  const [credentials, setCredentials] = useState({
    tempId: "",
    tempPw: "",
    permId: "",
    permPw: ""
  });

  const [editCred, setEditCred] = useState({
    id: "",
    pw: ""
  });

  const shareTempCredentials = async () => {
    if (!credentials.tempId || !credentials.tempPw) return;
    try {
      setLoading(true);
      await api.createCredentials(credentials.tempId, credentials.tempPw);
      setSuccessMsg("Temporary credentials shared to intern email.");
      setShowAddModal(false);
      setCredentials({ tempId: "", tempPw: "", permId: "", permPw: "" });
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (e) {
      alert("Share failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reporting Manager dropdown — fetch PMs from DB
  const [pmList, setPmList] = useState([]);
  useEffect(() => {
    api.getPMs().then((data) => setPmList(data || [])).catch(() => {});
  }, []);

  // Load interns from API
  useEffect(() => {
    const timer = setTimeout(() => fetchInterns(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (interns && interns.length) {
      setInternList(interns);
    }
  }, [interns]);

  const fetchInterns = async (q = "") => {
    try {
      setLoading(true);
      const data = await api.getHrInterns(q);
      setInternList(data);
    } catch (error) {
      console.error("Failed to fetch interns:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // CREATE INTERN (Temporary Login)
  // =========================================
  const createIntern = async () => {
    if (!formData.name || !formData.email) {
      alert("Name and Email are required!");
      return;
    }

    try {
      setLoading(true);
      const res = await api.createIntern({
        name: formData.name,
        email: formData.email,
        college: formData.college,
        department: formData.department,
        role: formData.role,
        startDate: formData.startDate || new Date().toISOString().split('T')[0],
        endDate: formData.endDate || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        manager: formData.manager || "HR Admin"
      });

      // Add new intern to list
      const newIntern = {
        id: res.intern?.tempId || res.intern?.id,
        name: formData.name,
        email: formData.email,
        department: formData.department,
        role: formData.role,
        status: "pending",
        joined: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        docs: 0,
        total: 5,
        college: formData.college,
        startDate: formData.startDate,
        endDate: formData.endDate,
        manager: formData.manager
      };

      setInternList([newIntern, ...internList]);

      // Show temporary credentials
      setCredentials({
        tempId: res.credentials?.id || res.intern?.tempId,
        tempPw: res.credentials?.password || "Temp@123",
        permId: "",
        permPw: ""
      });

      setShowAddModal(true);
      setSuccessMsg("Intern created with temporary login!");

      // Reset form
      setFormData({
        name: "", email: "", college: "", department: "Engineering",
        role: "Intern", startDate: "", endDate: "", manager: ""
      });

      setTimeout(() => setSuccessMsg(""), 4000);

      // Refresh data from backend
      if (refreshInterns) await refreshInterns();

    } catch (error) {
      console.error("Create intern error:", error);
      alert("Error creating intern: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // VERIFY INTERN (Approve/Reject)
  // =========================================
  const verifyIntern = async (id, action, reason = "") => {
    try {
      setLoading(true);
      const res = await api.verifyIntern(id, action, reason);

      const matchId = (intern) => intern.intern_id === id || intern.id === id || String(intern.id) === String(id);

      if (action === "approve") {
        const newPermId = res.permId || `INT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(4, '0')}`;
        const newPermPw = res.permPw || "Welcome@123";
        // Update intern list with verified status and permanent ID
        setInternList(displayList.map((intern) =>
        matchId(intern) ?
        {
          ...intern,
          id: res.permId || intern.intern_id || intern.id,
          intern_id: res.permId || intern.intern_id || intern.id,
          status: "verified",
          verifiedAt: new Date().toISOString()
        } :
        intern
        ));

        // Show permanent credentials
        setCredentials({
          tempId: id,
          tempPw: "",
          permId: newPermId,
          permPw: newPermPw
        });
        setEditCred({ id: newPermId, pw: newPermPw });

        setShowCredModal(true);
        setSuccessMsg(`Intern verified! Permanent ID: ${newPermId}`);
      } else {
        // Reject - update status
        setInternList(internList.map((intern) =>
        matchId(intern) ? { ...intern, status: "rejected" } : intern
        ));
        setSuccessMsg("Intern application rejected.");
      }

      setSelectedIntern(null);
      setTimeout(() => setSuccessMsg(""), 6000);

      // Refresh data
      if (refreshInterns) await refreshInterns();
      await fetchInterns();

    } catch (error) {
      console.error("Verify intern error:", error);
      alert("Error: " + (error.message || "Failed to verify intern"));
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // VIEW ONBOARDING SUBMISSION
  // =========================================
  const viewOnboarding = async (internId) => {
    try {
      setLoading(true);
      const data = await api.getOnboardingStatus(internId);
      setOnboardingData(data);
      setShowOnboardingModal(true);
    } catch (error) {
      console.error("Failed to fetch onboarding:", error);
      alert("Could not load onboarding details");
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // UPDATE CREDENTIALS
  // =========================================
  const updateCredentials = async () => {
    const finalId = String(editCred.id || credentials.permId || "").trim();
    const finalPw = String(editCred.pw || credentials.permPw || "").trim();
    if (!finalId || !finalPw) {
      alert("Intern ID and password are required.");
      return;
    }

    try {
      setLoading(true);
      await api.updateCredentials(finalId, finalPw);

      // Update intern list with new ID
      setInternList((prev) => prev.map((intern) =>
      intern.id === credentials.permId ? { ...intern, id: finalId } : intern
      ));

      setCredentials({ ...credentials, permId: finalId, permPw: finalPw });
      setShowCredModal(false);
      setSuccessMsg("Credential sent successfully.");
      alert("Credential sent successfully.");
      setTimeout(() => setSuccessMsg(""), 4000);

    } catch (error) {
      console.error("Update credentials error:", error);
      alert("Error: " + (error.message || "Failed to update credentials"));
    } finally {
      setLoading(false);
    }
  };

  const editInternEmail = async (intern) => {
    const current = intern.email || '';
    const next = window.prompt('Update intern email', current);
    if (!next || !next.trim() || next.trim() === current) return;
    try {
      setLoading(true);
      await api.updateHrUserEmail({ userId: intern.user_id || intern.userId || intern.id, empId: intern.intern_id || intern.id, email: next.trim() });
      setInternList((prev) => prev.map((it) =>
      (it.user_id || it.userId || it.id) === (intern.user_id || intern.userId || intern.id) ? { ...it, email: next.trim() } : it
      ));
      setSuccessMsg('Intern email updated');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e) {
      alert('Email update failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // RESEND ONBOARDING LINK
  // =========================================
  const openManagerModal = (intern) => {
    setManagerModal(intern);
    setSelectedManagerId("");
  };

  const assignInternManager = async () => {
    if (!managerModal) return;
    if (!selectedManagerId) { alert("Please select a manager."); return; }
    try {
      setLoading(true);
      const chosenManager = pmList.find((pm) => String(pm.id) === String(selectedManagerId));
      await api.updateHrUserManager({
        userId: managerModal.user_id || managerModal.userId || managerModal.id,
        empId:  managerModal.intern_id || managerModal.id,
        managerId: Number(selectedManagerId),
        managerRef: selectedManagerId,
      });
      const managerName = chosenManager?.name || "";
      setInternList((prev) => prev.map((it) =>
        (it.user_id || it.userId || it.id) === (managerModal.user_id || managerModal.userId || managerModal.id)
          ? { ...it, manager: managerName, manager_name: managerName }
          : it
      ));
      setSelectedIntern((prev) =>
        prev && (prev.user_id || prev.userId || prev.id) === (managerModal.user_id || managerModal.userId || managerModal.id)
          ? { ...prev, manager: managerName, manager_name: managerName }
          : prev
      );
      setSuccessMsg(`Manager assigned: ${managerName}`);
      setManagerModal(null);
      setSelectedManagerId("");
      if (refreshInterns) await refreshInterns();
      await fetchInterns(search);
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      alert("Manager assign failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDesignationModal = (intern) => {
    setDesignationModal(intern);
    setNewDesignation(intern.designation || intern.role || "");
  };

  const assignInternDesignation = async () => {
    if (!designationModal) return;
    const desig = newDesignation.trim();
    if (!desig) { alert("Please enter a designation."); return; }
    try {
      setLoading(true);
      await api.updateHrUserDesignation({
        userId: designationModal.user_id || designationModal.userId || designationModal.id,
        empId:  designationModal.intern_id || designationModal.id,
        designation: desig,
      });
      setInternList((prev) => prev.map((it) =>
        (it.user_id || it.userId || it.id) === (designationModal.user_id || designationModal.userId || designationModal.id)
          ? { ...it, designation: desig, role: desig }
          : it
      ));
      setSelectedIntern((prev) =>
        prev && (prev.user_id || prev.userId || prev.id) === (designationModal.user_id || designationModal.userId || designationModal.id)
          ? { ...prev, designation: desig, role: desig }
          : prev
      );
      setSuccessMsg(`Designation updated to "${desig}" for ${designationModal.name || ""}.`);
      setDesignationModal(null);
      setNewDesignation("");
      if (refreshInterns) await refreshInterns();
      await fetchInterns(search);
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      alert("Designation update failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOnboarding = async (internId) => {
    setResendModal(internId);
    setResendReason("");
    setResendSections([]);
  };

  // Document actions
  const viewDocument = async (docId) => {
    try {
      await api.previewDocById(docId);
    } catch (e) {alert("Could not load document: " + e.message);}
  };

  const downloadDocument = async (docId, docName) => {
    try {
      const data = await api.getDocUrl(docId);
      if (data.url) {await api.downloadFile(data.url, docName || "document");}
    } catch (e) {alert("Download failed: " + e.message);}
  };

  const reviewDocument = async (docId, status, comment = "") => {
    try {
      await api.reviewDoc(docId, status, comment);
      if (onboardingData?.user?.emp_id) {
        const data = await api.getOnboardingStatus(onboardingData.user.emp_id);
        setOnboardingData(data);
      }
      setRejectDocModal(null);
      setRejectReason("");
    } catch (e) {alert("Review failed: " + e.message);}
  };

  const resendWithReason = async () => {
    if (!resendModal) return;
    if (!resendSections.length) {
      alert("Please select at least one section to resend.");
      return;
    }
    try {
      await api.requestResubmission(resendModal, resendReason, resendSections);
      setSuccessMsg("Onboarding resent with reason!");
      setResendModal(null);
      setResendReason("");
      setResendSections([]);
      setShowOnboardingModal(false);
      refreshInterns?.();
      fetchInterns();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {alert("Resend failed: " + e.message);}
  };

  const docTypeLabels = {
    aadhaar: "Aadhaar Card", pan: "PAN Card", photo: "Passport Photo",
    driving_license: "Driving License", passport: "Passport",
    marksheet_10: "10th Marksheet", marksheet_12: "12th Marksheet",
    offer_letter: "Offer Letter", internship_letter: "Internship Letter",
    nda: "NDA", college_id: "College ID", other: "Other Document"
  };

  const docStatusColors = {
    available: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", label: "Uploaded" },
    pending: { bg: "#fff7ed", border: "#fed7aa", text: "#9a3412", label: "Pending Review" },
    rejected: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", label: "Rejected" },
    approved: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", label: "Approved" }
  };

  // =========================================
  // UI Helpers
  // =========================================
  const getStatusClass = (status) => {
    switch (status) {
      case "verified":return "chip-ok";
      case "active":return "chip-ok";
      case "rejected":return "chip-dn";
      case "submitted":return "chip-ip";
      case "pending":return "chip-wn";
      default:return "chip-wn";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "verified":return "verified";
      case "active":return "active";
      case "rejected":return "rejected";
      case "submitted":return "submitted";
      case "pending":return "pending";
      default:return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '—' || dateString === '-') return "—";
    // If already formatted like "Feb 2026", "Mar 2024", return as is
    if (/^[A-Z][a-z]{2}\s\d{4}$/.test(dateString)) return dateString;
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {return dateString;}
  };

  // ── Monthly derived data ──
  const availMonths = useMemo(() => {
    const set = new Set();
    internList.forEach(r => {
      const k = _mKey(r.user_created_at || r.startDate);
      if (k) set.add(k);
    });
    return ["all", ...Array.from(set).sort((a,b) => b.localeCompare(a))];
  }, [internList]);

  const displayList = useMemo(() => {
    if (monthFilter === "all") return internList;
    return internList.filter(r => _mKey(r.user_created_at || r.startDate) === monthFilter);
  }, [internList, monthFilter]);

  return (
    <div>
      {/* Header */}
      <div className="internspg-inline-1">
        <div className="internspg-inline-2">
          <div className="sec internspg-inline-3">Intern Management</div>
          <div className="internspg-inline-4">
            {II("search", 14, { style: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 } })}
            <input
              className="fi internspg-inline-5"
              placeholder="Search by name, ID or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>

          {/* ── Month select ── */}
          <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            style={{ padding:"7px 10px", border:"1px solid #e2e8f0", borderRadius:8,
              fontSize:12, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="all">All Months</option>
            {availMonths.filter(m=>m!=="all").map(m=>(
              <option key={m} value={m}>{_mLabel(m)}</option>
            ))}
          </select>

          {/* ── Monthly CSV dropdown ── */}
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowMonthDrop(v=>!v)}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px",
                background:"#fff", border:"1px solid #e2e8f0", borderRadius:8,
                fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>
              📅 Monthly CSV ▾
            </button>
            {showMonthDrop && (
              <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"#fff",
                border:"1px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.14)",
                zIndex:300, minWidth:230, overflow:"hidden" }}>
                <div style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:"#64748b",
                  borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                  MONTH-WISE DOWNLOAD
                </div>
                {availMonths.filter(m=>m!=="all").length === 0 ? (
                  <div style={{ padding:"12px", fontSize:12, color:"#94a3b8" }}>No data yet</div>
                ) : availMonths.filter(m=>m!=="all").map(m => {
                  const mRows = internList.filter(r => _mKey(r.user_created_at||r.startDate) === m);
                  return (
                    <button key={m}
                      onClick={() => { exportInternsCSV(mRows, `interns_${m}.csv`); setShowMonthDrop(false); }}
                      style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                        width:"100%", padding:"10px 14px", border:"none", background:"#fff",
                        cursor:"pointer", fontSize:13, borderBottom:"1px solid #f8fafc",
                        textAlign:"left" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                      onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                      <span style={{ fontWeight:600, color:"#1e293b" }}>{_mLabel(m)}</span>
                      <span style={{ fontSize:11, color:"#94a3b8" }}>{mRows.length} interns</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Download current view CSV ── */}
          <button onClick={() => exportInternsCSV(displayList, `interns${monthFilter!=="all"?"_"+monthFilter:""}.csv`)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
              background:"#0f172a", color:"#fff", border:"none", borderRadius:8,
              fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
            ⬇ Download CSV {displayList.length !== internList.length ? `(${displayList.length})` : ""}
          </button>

          <button
            className="btn btn-s"
            onClick={() => {
              setShowAddModal(true);
              setCredentials({ tempId: "", tempPw: "", permId: "", permPw: "" });
            }}
            disabled={loading}>
            {II("plus", 14)} Add Intern
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {successMsg &&
      <div className="toast">
          {II("chk", 16, { c: "#2563eb" })} {successMsg}
        </div>
      }

      {/* Loading State */}
      {loading &&
      <div className="internspg-inline-6">
          <div className="ob-pending-dot internspg-inline-7"></div>
          Loading...
        </div>
      }

      {/* ── Monthly filter banner ── */}
      {monthFilter !== "all" && (
        <div style={{ background:"linear-gradient(135deg,#1e1b4b,#4f46e5)", borderRadius:10,
          padding:"12px 18px", marginBottom:14, display:"flex", justifyContent:"space-between",
          alignItems:"center", color:"#fff" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>📅 {_mLabel(monthFilter)}</div>
            <div style={{ fontSize:11, opacity:.8, marginTop:2 }}>{displayList.length} interns added this month</div>
          </div>
          <div style={{ display:"flex", gap:14 }}>
            {["active","submitted","pending","rejected"].map(s => {
              const ct = displayList.filter(r=>r.status===s).length;
              return ct > 0 ? (
                <div key={s} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:18, fontWeight:800 }}>{ct}</div>
                  <div style={{ fontSize:9, opacity:.75, textTransform:"capitalize" }}>{s}</div>
                </div>
              ) : null;
            })}
          </div>
          <button onClick={() => exportInternsCSV(displayList, `interns_${monthFilter}.csv`)}
            style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            ⬇ Download Month CSV
          </button>
        </div>
      )}

      {/* Interns Table */}
      <div className="cd">
        <div className="cd-b internspg-inline-8">
          <table className="tbl">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>College</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Manager</th>
                <th>Docs</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayList.length === 0 ?
              <tr>
                  <td colSpan="11" className="internspg-inline-9">
                    {monthFilter !== "all" ? `No interns added in ${_mLabel(monthFilter)}` : "No interns found. Create your first intern!"}
                  </td>
                </tr> :

              internList.map((intern) =>
              <tr key={intern.id}>
                    <td className="internspg-inline-10">{intern.intern_id || intern.id}</td>
                    <td className="internspg-inline-11">{intern.name}</td>
                    <td className="internspg-inline-12">{intern.email || "—"}</td>
                    <td>{intern.department || intern.dept || "—"}</td>
                    <td>{intern.college || "—"}</td>
                    <td>{formatDate(intern.startDate)}</td>
                    <td>{formatDate(intern.endDate)}</td>
                    <td>{intern.manager || "—"}</td>
                    <td>
                      <span style={{ "--internspg-1-color":

                    (intern.docs || 0) === (intern.total || 11) ? "#2563eb" : "#d97706" }} className="internspg-dyn-1">
                    
                        {intern.docs || 0}/{intern.total || 11}
                      </span>
                    </td>
                    <td>
                      <span className={`chip ${getStatusClass(intern.status)}`}>
                        {getStatusLabel(intern.status)}
                      </span>
                    </td>
                    <td className="internspg-actions-cell">
                      <div className="internspg-inline-13">
                        {/* View Details Button */}
                        <button
                      className="btn-o btn-s internspg-act-btn"
                      onClick={() => setSelectedIntern(intern)}
                      title="View Details">
                      
                          {II("eye", 14)}
                        </button>
                        <button
                      className="btn-o btn-s internspg-act-btn"
                      onClick={() => editInternEmail(intern)}
                      disabled={loading}
                      title="Edit Email">
                      
                          {II("edit", 14)}
                        </button>
                        <button
                      className="btn-o btn-s internspg-act-btn"
                      onClick={() => openManagerModal(intern)}
                      disabled={loading}
                      title="Assign PM / Manager">
                          {II("people", 14)}
                        </button>
                        {/* View Onboarding Button - Show for pending/submitted/active */}
                        {["pending", "submitted", "onboarding", "active"].includes(intern.status) &&
                    <button
                      className="btn-o btn-s internspg-act-btn internspg-onboarding-btn"
                      onClick={() => viewOnboarding(intern.user_id || intern.userId)}
                      title="View Onboarding">
                      
                            {II("note", 14)}
                          </button>
                    }

                        {/* Approve/Reject: always visible for consistent 6 actions, enabled only for pending/submitted */}
                        <button
                      className="btn btn-s btn-ok internspg-act-btn"
                      onClick={() => {
                        if (!(intern.status === "pending" || intern.status === "submitted")) {
                          alert("Approve is allowed only for pending/submitted interns");
                          return;
                        }
                        verifyIntern(intern.intern_id || intern.id, "approve");
                      }}
                      disabled={loading}
                      title="Approve">
                      
                          {II("chk", 14)}
                        </button>
                        <button
                      className="btn btn-s btn-dn internspg-act-btn"
                      onClick={() => {
                        if (!(intern.status === "pending" || intern.status === "submitted")) {
                          alert("Reject is allowed only for pending/submitted interns");
                          return;
                        }
                        const reason = window.prompt("Enter rejection reason");
                        if (!reason || !reason.trim()) return;
                        verifyIntern(intern.intern_id || intern.id, "reject", reason.trim());
                      }}
                      disabled={loading}
                      title="Reject">
                      
                          {II("x", 14)}
                        </button>

                        {/* Credentials Button - allow reset for all non-exited users */}
                        {!["inactive", "deleted", "completed", "rejected"].includes(String(intern.status || "").toLowerCase()) &&
                    <button
                      className="btn btn-s internspg-inline-14 internspg-act-btn"

                      onClick={() => {
                        setCredentials({
                          ...credentials,
                          permId: intern.intern_id || intern.id,
                          permPw: "••••••••"
                        });
                        setEditCred({ id: intern.intern_id || intern.id, pw: "" });
                        setShowCredModal(true);
                      }}
                      title="View Credentials">
                      
                            {II("key", 14)}
                          </button>
                    }
                      </div>
                    </td>
                  </tr>
              )
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== MODAL: Intern Details ========== */}
      <HrModal
        open={selectedIntern !== null}
        onClose={() => setSelectedIntern(null)}
        title={`Intern: ${selectedIntern?.name || ""}`}>
        
        {selectedIntern &&
        <div>
            <div className="g2 mb14">
              {[
            ["ID", selectedIntern.intern_id || selectedIntern.id],
            ["Email", selectedIntern.email || "—"],
            ["Department", selectedIntern.department || selectedIntern.dept || "—"],
            ["College", selectedIntern.college || "—"],
            ["Start Date", formatDate(selectedIntern.startDate)],
            ["End Date", formatDate(selectedIntern.endDate)],
            ["Manager", selectedIntern.manager || "—"],
            ["Documents", `${selectedIntern.docs || 0}/${selectedIntern.total || 11}`],
            ["Status", getStatusLabel(selectedIntern.status)],
            ["Joined", selectedIntern.joined || "—"]].
            map(([label, value], i) =>
            <div key={i}>
                  <div className="internspg-inline-15">{label}</div>
                  <div className="internspg-inline-16">{value}</div>
                </div>
            )}
            </div>

            {/* Action Buttons */}
            <div className="internspg-inline-17">
              {(selectedIntern.status === "pending" || selectedIntern.status === "submitted") &&
            <>
                  <button
                className="btn btn-ok"
                onClick={() => verifyIntern(selectedIntern.intern_id || selectedIntern.id, "approve")}
                disabled={loading}>
                
                    {II("chk", 14)} Accept Intern
                  </button>
                  <button
                className="btn btn-dn"
                onClick={() => {
                  const reason = window.prompt("Enter rejection reason");
                  if (!reason || !reason.trim()) return;
                  verifyIntern(selectedIntern.intern_id || selectedIntern.id, "reject", reason.trim());
                }}
                disabled={loading}>
                
                    {II("x", 14)} Reject
                  </button>
                </>
            }
              <button className="btn-o" onClick={() => setSelectedIntern(null)}>Close</button>
            </div>
          </div>
        }
      </HrModal>

      {/* ========== MODAL: Create Intern ========== */}
      <HrModal
        open={showAddModal && !credentials.permId}
        onClose={() => setShowAddModal(false)}
        title="Create New Intern">
        
        <div>
          <div className="fg">
            <label className="fl">Full Name *</label>
            <input
              className="fi"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter intern full name" />
            
          </div>

          <div className="fg">
            <label className="fl">Email *</label>
            <input
              className="fi"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="intern@company.com" />
            
          </div>

          <div className="fg">
            <label className="fl">College / University</label>
            <input
              className="fi"
              value={formData.college}
              onChange={(e) => setFormData({ ...formData, college: e.target.value })}
              placeholder="e.g. VIT University" />
            
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Department</label>
              <select
                className="fsel"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}>
                
                <option>Engineering</option>
                <option>Design</option>
                <option>Marketing</option>
                <option>HR</option>
                <option>Sales</option>
                <option>Product</option>
              </select>
            </div>

            <div className="fg">
              <label className="fl">Role</label>
              <input
                className="fi"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="e.g. Frontend Intern" />
              
            </div>
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Start Date</label>
              <input
                className="fi"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
              
            </div>

            <div className="fg">
              <label className="fl">End Date</label>
              <input
                className="fi"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
              
            </div>
          </div>

          <div className="fg">
            <label className="fl">Reporting Manager</label>
            <select
              className="fsel"
              value={formData.manager}
              onChange={(e) => setFormData({ ...formData, manager: e.target.value })}>
              
              <option value="">Select Manager</option>
              {pmList.map((pm) =>
              <option key={pm.id} value={pm.name}>{pm.name} ({pm.emp_id})</option>
              )}
            </select>
          </div>

          <div className="internspg-inline-18">
            <button className="btn-o" onClick={() => setShowAddModal(false)}>Cancel</button>
            <button className="btn" onClick={createIntern} disabled={loading}>
              {loading ? "Creating..." : "Create Intern"}
            </button>
          </div>
        </div>
      </HrModal>

      {/* ========== MODAL: Temporary Credentials ========== */}
      <HrModal
        open={showAddModal && credentials.tempId}
        onClose={() => setShowAddModal(false)}
        title="Temporary Login Created">
        
        <div>
          <div className="internspg-inline-19">





            
            <div className="internspg-inline-20">
              ✅ Temporary Login Created!
            </div>

            <div className="internspg-inline-21">
              <span className="internspg-inline-22">Intern has been created. Share these credentials:</span>
            </div>

            <div className="internspg-inline-23">





              
              <div className="internspg-inline-24">
                🔐 TEMPORARY LOGIN DETAILS
              </div>

              <div className="internspg-inline-25">








                
                {credentials.tempId}
              </div>

              <div className="internspg-inline-26">







                
                {credentials.tempPw}
              </div>

              <div className="internspg-inline-27">
                Intern will use these to log in and complete onboarding.
              </div>
            </div>
          </div>

          <button
            className="btn internspg-inline-28"
            onClick={shareTempCredentials}
            disabled={loading}>

            
            {loading ? "Sharing..." : "Share"}
          </button>
        </div>
      </HrModal>

      {/* ========== MODAL: Permanent Credentials ========== */}
      <HrModal
        open={showCredModal}
        onClose={() => setShowCredModal(false)}
        title="Permanent Employee Credentials">
        
        <div>
          <div className="internspg-inline-29">




            
            <div className="internspg-inline-30">
              {II("key", 18, { c: "#4338ca" })} Permanent Login Details
            </div>

            <div className="fg">
              <label className="fl">Employee ID (Permanent)</label>
              <input
                className="fi internspg-inline-31"
                value={editCred.id || credentials.permId}
                onChange={(e) => setEditCred({ ...editCred, id: e.target.value })} />

              
            </div>

            <div className="fg">
              <label className="fl">Password</label>
              <input
                className="fi internspg-inline-32"
                type="text"
                value={editCred.pw || credentials.permPw}
                onChange={(e) => setEditCred({ ...editCred, pw: e.target.value })} />

              
            </div>

            <div className="internspg-inline-33">
              You can edit these credentials before saving. Share these with the employee.
            </div>
          </div>

          <div className="internspg-inline-34">
            <button className="btn-o" onClick={() => setShowCredModal(false)}>Cancel</button>
            <button className="btn" onClick={updateCredentials} disabled={loading}>
              {loading ? "Saving..." : "Save & Share"}
            </button>
          </div>
        </div>
      </HrModal>

      {/* ========== MODAL: Onboarding Submission ========== */}
      <HrModal
        open={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        title="Onboarding Submission Details">
        
        {onboardingData &&
        <div>
            <div className="internspg-inline-35">
              <div className="internspg-inline-36">
                <span className="internspg-inline-37">Submitted:</span>
                <span className="internspg-inline-38">
                  {formatDate(onboardingData.onboarding?.submitted_at)}
                </span>
              </div>
            </div>

            <div className="internspg-inline-39">
              {/* Personal Details */}
              <div className="internspg-inline-40">
                <div className="internspg-inline-41">
                  👤 Personal Details
                </div>
                <div className="internspg-inline-42">
                  {[
                ["Full Name", onboardingData.profile?.full_name],
                ["DOB", onboardingData.profile?.dob],
                ["Blood Group", onboardingData.profile?.blood_group],
                ["Gender", onboardingData.profile?.gender],
                ["Phone", onboardingData.profile?.phone],
                ["Email", onboardingData.profile?.personal_email],
                ["PAN", onboardingData.profile?.pan_number]].
                map(([label, value], i) =>
                <div key={i}>
                      <div className="internspg-inline-43">{label}</div>
                      <div className="internspg-inline-44">{value || "—"}</div>
                    </div>
                )}
                </div>
              </div>

              {/* Address */}
              <div className="internspg-inline-45">
                <div className="internspg-inline-46">
                  🏠 Address
                </div>
                <div className="internspg-inline-47">
                  <div>
                    <div className="internspg-inline-48">Current Address</div>
                    <div className="internspg-inline-49">
                      {[
                    onboardingData.profile?.current_address,
                    onboardingData.profile?.current_city,
                    onboardingData.profile?.current_state,
                    onboardingData.profile?.current_pincode].
                    filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="internspg-inline-50">Permanent Address</div>
                    <div className="internspg-inline-51">
                      {[
                    onboardingData.profile?.permanent_address,
                    onboardingData.profile?.permanent_city,
                    onboardingData.profile?.permanent_state,
                    onboardingData.profile?.permanent_pincode].
                    filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="internspg-inline-52">
                <div className="internspg-inline-53">
                  🏦 Bank Details
                </div>
                <div className="internspg-inline-54">
                  {[
                ["Bank Name", onboardingData.bank_details?.bank_name],
                ["Account No", onboardingData.bank_details?.account_number],
                ["IFSC Code", onboardingData.bank_details?.ifsc_code],
                ["Branch", onboardingData.bank_details?.branch]].
                map(([label, value], i) =>
                <div key={i}>
                      <div className="internspg-inline-55">{label}</div>
                      <div className="internspg-inline-56">{value || "—"}</div>
                    </div>
                )}
                </div>
              </div>

              {/* Education */}
              {onboardingData.education && onboardingData.education.length > 0 &&
            <div>
                  <div className="internspg-inline-57">
                    🎓 Education
                  </div>
                  {onboardingData.education.map((edu, idx) =>
              <div key={idx} className="internspg-inline-58">
                      <div className="internspg-inline-59">{edu.degree} {edu.specialization && `- ${edu.specialization}`}</div>
                      <div className="internspg-inline-60">{edu.institution}</div>
                      <div className="internspg-inline-61">
                        <span className="internspg-inline-62">
                          {edu.start_year} - {edu.is_pursuing ? "Present" : edu.end_year}
                        </span>
                        <span className="internspg-inline-63">
                          {edu.score} {edu.score_type}
                        </span>
                      </div>
                    </div>
              )}
                </div>
            }
            </div>

            {/* ═══════ DOCUMENTS VERIFICATION ═══════ */}
            <DocumentVerification
            documents={onboardingData.documents || []}
            canUpload={true}
            targetUserId={onboardingData.user?.id || onboardingData.profile?.user_id || selectedIntern?.user_id || selectedIntern?.userId || null}
            targetEmpId={onboardingData.user?.emp_id || selectedIntern?.intern_id || selectedIntern?.id || null}
            onRefresh={async () => {
              if (onboardingData?.user?.emp_id) {
                const data = await api.getOnboardingStatus(onboardingData.user.emp_id);
                setOnboardingData(data);
              }
            }} />
          

            <div className="internspg-inline-64">
              <button className="btn-o" onClick={() => setShowOnboardingModal(false)}>Close</button>
              {(onboardingData.status === "ONBOARDING_SUBMITTED" || onboardingData.onboarding?.status === "submitted") &&
            <>
                  <button

                onClick={() => {setResendModal(selectedIntern?.intern_id || onboardingData.user?.emp_id);setResendReason("");setResendSections([]);}} className="internspg-inline-65">
                🔄 Resend with Reason</button>
                  <button className="btn btn-ok" onClick={() => {
                const rejected = (onboardingData.documents || []).filter((d) => d.status === "rejected");
                if (rejected.length > 0) {alert(`Cannot approve: ${rejected.length} document(s) still rejected.`);return;}
                setShowOnboardingModal(false);
                verifyIntern(selectedIntern?.intern_id || onboardingData.user?.emp_id, "approve");
              }}>{II("chk", 14)} Approve All</button>
                </>
            }
            </div>
          </div>
        }
      </HrModal>

      {/* ========== MODAL: Assign PM / Manager for Intern ========== */}
      <HrModal
        open={managerModal !== null}
        onClose={() => { setManagerModal(null); setSelectedManagerId(""); }}
        title={`Assign PM / Manager: ${managerModal?.name || ""}`}>
        {managerModal &&
          <div>
            <div className="fg">
              <label className="fl">Current Manager</label>
              <div style={{ padding: "6px 10px", background: "#f4f7fc", borderRadius: 4, fontSize: 13, color: "#5b7a9e", marginBottom: 12 }}>
                {managerModal.manager || managerModal.manager_name || "None assigned"}
              </div>
              <label className="fl">Select New PM / Manager</label>
              <select
                className="fsel"
                value={selectedManagerId}
                onChange={(e) => setSelectedManagerId(e.target.value)}>
                <option value="">— Select Manager —</option>
                {pmList.map((pm) =>
                  <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>
                )}
              </select>
              {pmList.length === 0 && (
                <div style={{ fontSize: 11, color: "#d97706", marginTop: 6 }}>
                  No active PMs found. Create a PM account first.
                </div>
              )}
            </div>
            <div className="employeespg-inline-17">
              <button className="btn-o" onClick={() => { setManagerModal(null); setSelectedManagerId(""); }}>Cancel</button>
              <button className="btn" onClick={assignInternManager} disabled={loading || !selectedManagerId}>
                {loading ? "Saving..." : "Assign"}
              </button>
            </div>
          </div>
        }
      </HrModal>

      {/* Resend with Reason Modal */}
      {resendModal &&
      <div className="internspg-inline-66">
          <div className="internspg-inline-67">
            <div className="internspg-inline-68">🔄 Resend Onboarding</div>
            <div className="internspg-inline-69">Select sections to resubmit:</div>
            <div className="internspg-inline-70">
              {["Personal Details", "Address", "Bank Details", "Education", "Documents", "Declaration"].map((s) =>
            <label key={s} style={{ "--internspg-2-border": `1px solid ${resendSections.includes(s) ? "#f59e0b" : "#e2e8f0"}`, "--internspg-2-background": resendSections.includes(s) ? "#fffbeb" : "#fff" }} className="internspg-dyn-2">
                  <input type="checkbox" checked={resendSections.includes(s)} onChange={(e) => {if (e.target.checked) setResendSections((p) => [...p, s]);else setResendSections((p) => p.filter((x) => x !== s));}} className="internspg-inline-71" />{s}
                </label>
            )}
            </div>
            <textarea value={resendReason} onChange={(e) => setResendReason(e.target.value)} placeholder="Reason for resend..." rows={3} className="internspg-inline-72" />
            <div className="internspg-inline-73">
              <button onClick={() => {setResendModal(null);setResendReason("");setResendSections([]);}} className="internspg-inline-74">Cancel</button>
              <button onClick={resendWithReason} disabled={!resendReason.trim()} style={{ "--internspg-3-cursor": resendReason.trim() ? "pointer" : "not-allowed", "--internspg-3-background": resendReason.trim() ? "#f59e0b" : "#fde68a" }} className="internspg-dyn-3">🔄 Resend</button>
            </div>
          </div>
        </div>
      }
    </div>);

}

export default InternsPg;
